import { useState } from 'react';
import { useChatStore } from '../../stores/chat';
import { useAuthStore } from '../../stores/auth';
import type { Thread, AppUser } from '../../lib/api';
import { formatDistanceToNowStrict } from 'date-fns';
import { useMobile } from '../../hooks/useMobile';
import SegmentedControl from '../ui/SegmentedControl';
import { Avatar, PresenceDot } from '../ui/Avatar';
import { X, Check, Plus } from '../../lib/icons';

type InternalTab = 'org' | 'direct' | 'groups';

interface Props {
  onSelectThread: (id: string) => void;
  onStartDm: (userId: string) => Promise<void>;
  onCreateGroup: (name: string, participantIds: string[]) => Promise<void>;
}

function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name.split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2);
}

function relativeTime(ts: string | null | undefined): string {
  if (!ts) return '';
  try { return formatDistanceToNowStrict(new Date(ts), { addSuffix: false }); }
  catch { return ''; }
}

export default function InternalSidebar({ onSelectThread, onStartDm, onCreateGroup }: Props) {
  const isMobile = useMobile();
  const threads = useChatStore((s) => s.threads);
  const activeThreadId = useChatStore((s) => s.activeThreadId);
  const users = useChatStore((s) => s.users);
  const onlineUserIds = useChatStore((s) => s.onlineUserIds);
  const currentUser = useAuthStore((s) => s.user);

  const [tab, setTab] = useState<InternalTab>('org');
  // 'picker' mode: 'direct' | 'group' | null
  const [pickerMode, setPickerMode] = useState<'direct' | 'group' | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<AppUser[]>([]);
  const [groupName, setGroupName] = useState('');
  const [groupNameStep, setGroupNameStep] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dmLoadingId, setDmLoadingId] = useState<string | null>(null);

  const orgThread = threads.find((t) => t.type === 'org');
  const directThreads = threads.filter((t) => t.type === 'direct');
  const groupThreads = threads.filter((t) => t.type === 'internal');

  const otherUsers = users.filter((u) => u.id !== currentUser?.id);
  const sortedUsers = [...otherUsers].sort((a, b) => {
    const aOnline = onlineUserIds.has(a.id) ? 0 : 1;
    const bOnline = onlineUserIds.has(b.id) ? 0 : 1;
    if (aOnline !== bOnline) return aOnline - bOnline;
    return a.name.localeCompare(b.name);
  });

  function closePicker() {
    setPickerMode(null);
    setSelectedUsers([]);
    setGroupName('');
    setGroupNameStep(false);
    setError(null);
  }

  function openPicker(mode: 'direct' | 'group') {
    closePicker();
    setPickerMode(mode);
  }

  async function handleDirectClick(user: AppUser) {
    setDmLoadingId(user.id);
    setError(null);
    try {
      await onStartDm(user.id);
      closePicker();
      setTab('direct');
    } catch {
      setError('Failed to open DM');
    } finally {
      setDmLoadingId(null);
    }
  }

  function toggleGroupUser(user: AppUser) {
    setSelectedUsers((prev) =>
      prev.some((u) => u.id === user.id)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user]
    );
  }

  async function handleCreateGroup() {
    if (!groupName.trim() || selectedUsers.length === 0) return;
    setLoadingAction(true);
    setError(null);
    try {
      await onCreateGroup(groupName.trim(), selectedUsers.map((u) => u.id));
      closePicker();
      setTab('groups');
    } catch {
      setError('Failed to create group');
    } finally {
      setLoadingAction(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      {/* Inner subtabs */}
      <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--shell-border)', flexShrink: 0 }}>
        <SegmentedControl
          size="sm"
          fullWidth
          value={tab}
          onChange={(t) => { setTab(t); closePicker(); }}
          options={[
            { value: 'org', label: 'Organisation' },
            { value: 'direct', label: 'Direct' },
            { value: 'groups', label: 'Groups' },
          ]}
        />
      </div>

      {/* User picker overlay */}
      {pickerMode && (
        <UserPicker
          mode={pickerMode}
          users={sortedUsers}
          onlineUserIds={onlineUserIds}
          selectedUsers={selectedUsers}
          groupName={groupName}
          groupNameStep={groupNameStep}
          loadingAction={loadingAction}
          dmLoadingId={dmLoadingId}
          error={error}
          onClose={closePicker}
          onDirectClick={handleDirectClick}
          onToggleGroupUser={toggleGroupUser}
          onGroupNameChange={setGroupName}
          onGroupNameStepChange={setGroupNameStep}
          onCreateGroup={handleCreateGroup}
        />
      )}

      {/* Org tab */}
      {!pickerMode && tab === 'org' && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {orgThread ? (
            <OrgThreadRow
              thread={orgThread}
              active={activeThreadId === orgThread.id}
              onClick={() => onSelectThread(orgThread.id)}
            />
          ) : (
            <EmptyState label="No organisation thread" />
          )}
          {/* Desktop-only member roster (4e) — nothing else to scroll to in a single pinned org thread */}
          {!isMobile && (
            <>
              <SectionHeader label={`Everyone · ${otherUsers.length + 1}`} />
              {[...(currentUser ? [currentUser] : []), ...sortedUsers].map((u) => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 14px' }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar name={u.name} initialsOverride={u.avatarInitials ?? u.avatar_initials} size={28} />
                    <div style={{ position: 'absolute', bottom: -1, right: -1 }}>
                      <PresenceDot online={u.id === currentUser?.id || onlineUserIds.has(u.id)} />
                    </div>
                  </div>
                  <span style={{ fontSize: '0.8rem', color: 'var(--shell-muted)' }}>
                    {u.name}{u.id === currentUser?.id ? ' (you)' : ''}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Direct tab */}
      {!pickerMode && tab === 'direct' && (
        <ListWithFooter
          isMobile={isMobile}
          footerLabel="New Message"
          onFooterClick={() => openPicker('direct')}
        >
          <SectionHeader label="Direct Messages">
            {!isMobile && <PlusButton onClick={() => openPicker('direct')} title="Start a direct message" />}
          </SectionHeader>
          {directThreads.length === 0
            ? <EmptyState label="No direct messages yet" />
            : directThreads.map((t) => (
                <ThreadRow
                  key={t.id}
                  thread={t}
                  active={activeThreadId === t.id}
                  onClick={() => onSelectThread(t.id)}
                  currentUserId={currentUser?.id ?? ''}
                  onlineUserIds={onlineUserIds}
                />
              ))
          }
        </ListWithFooter>
      )}

      {/* Groups tab */}
      {!pickerMode && tab === 'groups' && (
        <ListWithFooter
          isMobile={isMobile}
          footerLabel="New Group"
          onFooterClick={() => openPicker('group')}
        >
          <SectionHeader label="Group Chats">
            {!isMobile && <PlusButton onClick={() => openPicker('group')} title="Create a group chat" />}
          </SectionHeader>
          {groupThreads.length === 0
            ? <EmptyState label="No group chats yet" />
            : groupThreads.map((t) => (
                <ThreadRow
                  key={t.id}
                  thread={t}
                  active={activeThreadId === t.id}
                  onClick={() => onSelectThread(t.id)}
                  currentUserId={currentUser?.id ?? ''}
                  onlineUserIds={onlineUserIds}
                />
              ))
          }
        </ListWithFooter>
      )}
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────────────── */

/** Scrollable thread list; on mobile the "new chat" action is a full-width button pinned at the bottom (like Add Task). */
function ListWithFooter({ isMobile, footerLabel, onFooterClick, children }: {
  isMobile: boolean; footerLabel: string; onFooterClick: () => void; children: React.ReactNode;
}) {
  return (
    <>
      <div style={{ flex: 1, overflowY: 'auto' }}>{children}</div>
      {isMobile && (
        <div style={{ padding: '10px', borderTop: '1px solid var(--shell-border)', flexShrink: 0 }}>
          <button
            onClick={onFooterClick}
            style={{
              width: '100%', padding: '10px 0', borderRadius: 5,
              background: 'var(--v1)', border: 'none', color: 'var(--text-on-accent)',
              fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            }}
          >
            <Plus size={14} strokeWidth={2.2} /> {footerLabel}
          </button>
        </div>
      )}
    </>
  );
}

function SectionHeader({ label, children }: { label: string; children?: React.ReactNode }) {
  return (
    <div style={{
      fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.1em', color: 'var(--shell-xs)',
      padding: '8px 14px 4px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <span>{label}</span>
      {children}
    </div>
  );
}

function PlusButton({ onClick, title }: { onClick: () => void; title: string }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        width: 22, height: 22, borderRadius: 6,
        border: 'none',
        background: 'var(--accent)', color: 'var(--text-on-accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', transition: 'background .12s', flexShrink: 0,
      }}
    ><Plus size={13} strokeWidth={2} /></button>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div style={{ padding: '24px 16px', color: 'var(--shell-xs)', fontSize: '0.78rem', textAlign: 'center' }}>
      {label}
    </div>
  );
}

function UserPicker({
  mode, users, onlineUserIds, selectedUsers, groupName, groupNameStep,
  loadingAction, dmLoadingId, error,
  onClose, onDirectClick, onToggleGroupUser,
  onGroupNameChange, onGroupNameStepChange, onCreateGroup,
}: {
  mode: 'direct' | 'group';
  users: AppUser[];
  onlineUserIds: Set<string>;
  selectedUsers: AppUser[];
  groupName: string;
  groupNameStep: boolean;
  loadingAction: boolean;
  dmLoadingId: string | null;
  error: string | null;
  onClose: () => void;
  onDirectClick: (u: AppUser) => void;
  onToggleGroupUser: (u: AppUser) => void;
  onGroupNameChange: (v: string) => void;
  onGroupNameStepChange: (v: boolean) => void;
  onCreateGroup: () => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = users.filter((u) => u.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Picker header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', borderBottom: '1px solid var(--shell-border)', flexShrink: 0,
      }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--shell-muted)' }}>
          {mode === 'direct' ? 'New Direct Message' : groupNameStep ? 'Name your group' : 'Select Members'}
        </span>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'var(--shell-xs)',
          cursor: 'pointer', padding: 2, display: 'flex',
        }}><X size={15} strokeWidth={1.8} /></button>
      </div>

      {/* Group name step */}
      {mode === 'group' && groupNameStep ? (
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--shell-xs)' }}>
            {selectedUsers.length} member{selectedUsers.length !== 1 ? 's' : ''} selected
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {selectedUsers.map((u) => (
              <span key={u.id} style={{
                fontSize: '0.72rem', padding: '2px 8px', borderRadius: 3,
                background: 'var(--accent-tint)', color: 'var(--v2)',
                border: '1px solid var(--active-border)',
              }}>{u.name}</span>
            ))}
          </div>
          <input
            autoFocus
            placeholder="Group name…"
            value={groupName}
            onChange={(e) => onGroupNameChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && groupName.trim()) onCreateGroup(); }}
            style={{
              background: 'var(--vs2)', border: '1px solid var(--vb)',
              borderRadius: 4, padding: '7px 10px',
              color: 'var(--text)', fontSize: '0.83rem',
              outline: 'none',
            }}
          />
          {error && <div style={{ fontSize: '0.72rem', color: 'var(--red)' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => onGroupNameStepChange(false)}
              style={{
                flex: 1, padding: '6px 0', borderRadius: 4,
                background: 'transparent', border: '1px solid var(--vb)',
                color: 'var(--shell-xs)', fontSize: '0.78rem', cursor: 'pointer',
              }}
            >Back</button>
            <button
              onClick={onCreateGroup}
              disabled={!groupName.trim() || loadingAction}
              style={{
                flex: 2, padding: '6px 0', borderRadius: 4,
                background: groupName.trim() ? 'var(--v1)' : 'var(--vb2)',
                border: 'none', color: 'var(--text-on-accent)',
                fontSize: '0.78rem', fontWeight: 700,
                cursor: groupName.trim() && !loadingAction ? 'pointer' : 'not-allowed',
              }}
            >{loadingAction ? 'Creating…' : 'Create Group'}</button>
          </div>
        </div>
      ) : (
        <>
          {/* Search */}
          <div style={{ padding: '8px 10px', flexShrink: 0 }}>
            <input
              autoFocus
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%', background: 'var(--vs2)',
                border: '1px solid var(--vb)', borderRadius: 4,
                padding: '5px 8px', color: 'var(--text)',
                fontSize: '0.8rem', outline: 'none',
              }}
            />
          </div>

          {/* Group selection chips */}
          {mode === 'group' && selectedUsers.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '4px 10px', flexShrink: 0 }}>
              {selectedUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => onToggleGroupUser(u)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: '0.72rem', padding: '2px 8px', borderRadius: 3,
                    background: 'var(--accent-tint)', color: 'var(--v2)',
                    border: '1px solid var(--active-border)', cursor: 'pointer',
                  }}
                >{u.name} <X size={10} strokeWidth={2} /></button>
              ))}
            </div>
          )}

          {/* User list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <div style={{ padding: '20px 14px', color: 'var(--shell-xs)', fontSize: '0.78rem', textAlign: 'center' }}>
                No results
              </div>
            )}
            {filtered.map((u) => {
              const online = onlineUserIds.has(u.id);
              const selected = mode === 'group' && selectedUsers.some((s) => s.id === u.id);
              const loading = dmLoadingId === u.id;
              return (
                <div
                  key={u.id}
                  onClick={() => mode === 'direct' ? onDirectClick(u) : onToggleGroupUser(u)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9,
                    padding: '8px 14px', cursor: loading ? 'wait' : 'pointer',
                    borderBottom: '1px solid var(--row-divider)',
                    background: selected ? 'var(--active-bg)' : 'transparent',
                    borderLeft: selected ? '2px solid var(--active-border)' : '2px solid transparent',
                    transition: 'background .12s',
                  }}
                >
                  {/* Avatar + presence */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar name={u.name} initialsOverride={u.avatarInitials ?? u.avatar_initials} size={30} />
                    <div style={{ position: 'absolute', bottom: -1, right: -1 }}>
                      <PresenceDot online={online} />
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--shell-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {u.name}
                    </div>
                    {u.role && (
                      <div style={{ fontSize: '0.68rem', color: 'var(--shell-xs)', textTransform: 'capitalize' }}>{u.role}</div>
                    )}
                  </div>
                  {loading && <span style={{ fontSize: '0.72rem', color: 'var(--shell-xs)' }}>…</span>}
                  {mode === 'group' && selected && (
                    <Check size={15} strokeWidth={2} style={{ color: 'var(--v1)' }} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Group next step footer */}
          {mode === 'group' && (
            <div style={{ padding: '8px 10px', borderTop: '1px solid var(--shell-border)', flexShrink: 0 }}>
              {error && <div style={{ fontSize: '0.72rem', color: 'var(--red)', marginBottom: 6 }}>{error}</div>}
              <button
                onClick={() => selectedUsers.length > 0 && onGroupNameStepChange(true)}
                disabled={selectedUsers.length === 0}
                style={{
                  width: '100%', padding: '7px 0', borderRadius: 4,
                  background: selectedUsers.length > 0 ? 'var(--v1)' : 'var(--vb2)',
                  border: 'none', color: 'var(--text-on-accent)',
                  fontSize: '0.78rem', fontWeight: 700,
                  cursor: selectedUsers.length > 0 ? 'pointer' : 'not-allowed',
                }}
              >
                {selectedUsers.length === 0 ? 'Select members' : `Next (${selectedUsers.length} selected)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function dmDisplayName(thread: Thread, currentUserId: string): string {
  if (thread.name) return thread.name;
  const others = (thread.participants ?? []).filter((p) => p.user_id !== currentUserId);
  if (others.length === 0) return 'Direct Message';
  return others.map((p) => p.name ?? p.contact_name ?? '?').join(', ');
}

function dmAvatarInitials(thread: Thread, currentUserId: string): string {
  if (thread.name) return initials(thread.name);
  const others = (thread.participants ?? []).filter((p) => p.user_id !== currentUserId);
  if (others.length === 0) return '?';
  if (others.length === 1) return initials(others[0].name ?? others[0].contact_name);
  return others.slice(0, 3).map((p) => (p.name ?? p.contact_name ?? '?')[0].toUpperCase()).join('·');
}

/** The other participant's user_id in a 1:1 DM — used to resolve a presence dot. */
function dmOtherUserId(thread: Thread, currentUserId: string): string | undefined {
  const others = (thread.participants ?? []).filter((p) => p.user_id !== currentUserId);
  return others.length === 1 ? others[0].user_id : undefined;
}

function ThreadRow({ thread, active, onClick, currentUserId, onlineUserIds }: {
  thread: Thread; active: boolean; onClick: () => void; currentUserId: string; onlineUserIds: Set<string>;
}) {
  const unread = parseInt(thread.unread_count ?? '0') > 0;
  const isDirect = thread.type === 'direct';
  const displayName = isDirect
    ? dmDisplayName(thread, currentUserId)
    : (thread.name ?? 'Group');
  const avatarContent = isDirect
    ? dmAvatarInitials(thread, currentUserId)
    : initials(displayName);
  const avatarStyle: React.CSSProperties = isDirect
    ? { background: 'var(--navy-900)', color: 'var(--blue)' }
    : { background: 'var(--vb2)', color: 'var(--teal-300)' };
  const otherUserId = isDirect ? dmOtherUserId(thread, currentUserId) : undefined;

  return (
    <div
      onClick={onClick}
      style={{
        padding: '9px 14px', cursor: 'pointer',
        borderBottom: '1px solid var(--row-divider)',
        display: 'flex', gap: 9, alignItems: 'flex-start',
        borderLeft: active ? '2px solid var(--active-border)' : '2px solid transparent',
        background: active ? 'var(--active-bg)' : 'transparent',
        transition: 'background .12s',
      }}
    >
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{
          width: 34, height: 34, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.75rem', fontWeight: 700,
          ...avatarStyle,
        }}>
          {avatarContent}
        </div>
        {otherUserId && (
          <div style={{ position: 'absolute', bottom: -1, right: -1 }}>
            <PresenceDot online={onlineUserIds.has(otherUserId)} />
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--shell-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {displayName}
        </div>
        <div style={{ fontSize: '0.74rem', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--shell-xs)' }}>
          {thread.last_message_sender && `${thread.last_message_sender}: `}
          {thread.last_message ?? 'No messages yet'}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <span style={{ fontSize: '0.68rem', color: 'var(--shell-xs)' }}>
          {relativeTime(thread.last_message_at)}
        </span>
        <div style={{ width: 7, height: 7, borderRadius: 2, background: unread ? 'var(--v1)' : 'transparent' }} />
      </div>
    </div>
  );
}

function OrgThreadRow({ thread, active, onClick }: { thread: Thread; active: boolean; onClick: () => void }) {
  const unread = parseInt(thread.unread_count ?? '0') > 0;
  return (
    <div
      onClick={onClick}
      style={{
        padding: '9px 14px', cursor: 'pointer',
        borderBottom: '1px solid var(--row-divider)',
        display: 'flex', gap: 9, alignItems: 'flex-start',
        borderLeft: active ? '2px solid var(--active-border)' : '2px solid transparent',
        background: active ? 'var(--active-bg)' : 'transparent',
        transition: 'background .12s',
      }}
    >
      <Avatar isOrg tone="org" size={34} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--shell-muted)' }}>@all — Org</div>
        <div style={{ fontSize: '0.74rem', marginTop: 2, color: 'var(--shell-xs)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {thread.last_message ?? 'Company-wide broadcast'}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <span style={{ fontSize: '0.68rem', color: 'var(--shell-xs)' }}>{relativeTime(thread.last_message_at)}</span>
        <div style={{ width: 7, height: 7, borderRadius: 2, background: unread ? 'var(--v1)' : 'transparent' }} />
      </div>
    </div>
  );
}
